import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} PcCartItem
 * @property {number} id - Variant ID
 * @property {number} [quantity]
 * @property {Record<string, string>} [properties]
 * @property {string} [title]
 * @property {string} [image]
 * @property {string} [priceLabel]
 */

/**
 * Apply a Shopify discount code to the current cart (must exist in Admin → Discounts).
 * @param {string} code
 * @returns {Promise<object|null>}
 */
export async function pcApplyDiscountCode(code) {
  const trimmed = String(code || '').trim();
  if (!trimmed) return null;

  const root = window.Shopify?.routes?.root || '/';
  const cartRes = await fetch(`${root}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  const cart = cartRes.ok ? await cartRes.json() : null;
  const existing = Array.isArray(cart?.discount_codes)
    ? cart.discount_codes.map((d) => d.code).filter(Boolean)
    : [];
  if (existing.some((c) => c.toLowerCase() === trimmed.toLowerCase())) {
    return cart;
  }

  const allCodes = [...existing, trimmed];
  const response = await fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ discount: allCodes.join(',') }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.description || err.message || 'Could not apply discount');
  }

  const updated = await response.json();
  const applied = Array.isArray(updated.discount_codes)
    ? updated.discount_codes.find(
        (d) => d.code?.toLowerCase() === trimmed.toLowerCase() && d.applicable === false
      )
    : null;
  if (applied) {
    throw new Error(`Discount code "${trimmed}" is not applicable. Check Admin → Discounts.`);
  }

  return updated;
}

/**
 * @param {PcCartItem | PcCartItem[]} items
 * @param {{ showNotification?: boolean, discountCode?: string }} [options]
 */
export async function pcAddToCart(items, options = {}) {
  const list = (Array.isArray(items) ? items : [items]).map((item) => {
    const row = {
      id: Number(item.id),
      quantity: Number(item.quantity || 1),
    };
    if (item.properties && typeof item.properties === 'object') {
      row.properties = item.properties;
    }
    return row;
  });

  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: list }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.description || err.message || 'Could not add to cart');
  }

  const added = await response.json();

  let cart = null;
  let discountError = null;
  if (options.discountCode) {
    try {
      cart = await pcApplyDiscountCode(options.discountCode);
    } catch (error) {
      discountError = error instanceof Error ? error : new Error(String(error));
    }
  }

  if (!cart) {
    const cartResponse = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    cart = cartResponse.ok ? await cartResponse.json() : null;
  }

  const deferred = CartLinesUpdateEvent.createPromise?.() || { promise: Promise.resolve(), resolve: () => {}, reject: () => {} };

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'paris-corner',
      lines: list.map((item) => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
      })),
      promise: deferred.promise,
    })
  );

  deferred.resolve?.({
    cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse?.(cart) : undefined,
    detail: { items: cart?.items, source: 'paris-corner-cart' },
  });

  if (options.showNotification !== false) {
    const meta = Array.isArray(items) ? items[0] : items;
    showCartNotification({
      title: meta.title || added.items?.[0]?.title || added.title || 'Added to cart',
      image: meta.image || added.items?.[0]?.image || added.image,
      priceLabel: meta.priceLabel,
    });
  }

  return { added, cart, discountError };
}

/**
 * @param {{ title?: string, image?: string, priceLabel?: string }} product
 */
export function showCartNotification(product = {}) {
  const root = document.querySelector('[data-pc-cart-notification]');
  if (!root) return;

  const titleEl = root.querySelector('[data-pc-cart-notification-title]');
  const imageEl = root.querySelector('[data-pc-cart-notification-image]');
  const priceEl = root.querySelector('[data-pc-cart-notification-price]');

  if (titleEl) titleEl.textContent = product.title || 'Added to cart';
  if (priceEl) {
    priceEl.textContent = product.priceLabel || '';
    priceEl.hidden = !product.priceLabel;
  }
  if (imageEl) {
    if (product.image) {
      imageEl.src = product.image;
      imageEl.alt = product.title || '';
      imageEl.hidden = false;
    } else {
      imageEl.hidden = true;
    }
  }

  root.classList.add('is-visible');
  root.setAttribute('aria-hidden', 'false');

  clearTimeout(root._pcHideTimer);
  root._pcHideTimer = setTimeout(() => hideCartNotification(), 8000);
}

export function hideCartNotification() {
  const root = document.querySelector('[data-pc-cart-notification]');
  if (!root) return;
  root.classList.remove('is-visible');
  root.setAttribute('aria-hidden', 'true');
}

/**
 * @param {HTMLElement} btn
 * @param {'idle' | 'loading' | 'added'} state
 */
function setAtcButtonState(btn, state) {
  if (!(btn instanceof HTMLElement)) return;

  if (!btn.dataset.idleText) {
    const labelEl = btn.querySelector('.pc-atc-btn__label');
    btn.dataset.idleText = (labelEl?.textContent || btn.textContent || 'Add to cart').trim();
  }

  const idleText = btn.dataset.idleText || 'Add to cart';
  const loadingText = btn.dataset.loadingText || 'Adding...';
  const addedText = btn.dataset.addedText || 'Added';

  let next = idleText;
  if (state === 'loading') next = loadingText;
  if (state === 'added') next = addedText;

  const labelEl = btn.querySelector('.pc-atc-btn__label');
  if (labelEl) {
    labelEl.textContent = next;
  } else {
    btn.textContent = next;
  }

  const checkEl = btn.querySelector('.pc-atc-btn__check');
  if (checkEl instanceof HTMLElement) {
    checkEl.hidden = state !== 'added';
  }

  btn.classList.toggle('pc-atc-btn--added', state === 'added');
  btn.dataset.state = state;

  if (state === 'loading') {
    btn.disabled = true;
  } else {
    // Stay clickable so qty can increase; visual stays "Added" after first success.
    btn.disabled = false;
  }
}

/**
 * Mark every ATC button whose variant is already in the cart as "Added".
 * @param {Set<string> | string[]} variantIds
 */
function markAtcButtonsInCart(variantIds) {
  const ids = variantIds instanceof Set ? variantIds : new Set(variantIds.map(String));
  document.querySelectorAll('[data-pc-atc][data-variant-id]').forEach((btn) => {
    if (!(btn instanceof HTMLElement)) return;
    if (ids.has(String(btn.dataset.variantId))) {
      setAtcButtonState(btn, 'added');
    }
  });
}

/**
 * After a successful add, update matching ATC buttons site-wide.
 * @param {string | number} variantId
 */
function markVariantAdded(variantId) {
  const id = String(variantId);
  document.querySelectorAll('[data-pc-atc][data-variant-id]').forEach((btn) => {
    if (!(btn instanceof HTMLElement)) return;
    if (String(btn.dataset.variantId) === id) {
      setAtcButtonState(btn, 'added');
    }
  });
}

async function syncAtcButtonsWithCart() {
  try {
    const response = await fetch(
      window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart.js` : '/cart.js',
      { headers: { Accept: 'application/json' }, credentials: 'same-origin' }
    );
    if (!response.ok) return;
    const cart = await response.json();
    const ids = (cart.items || []).map((item) => String(item.variant_id));
    markAtcButtonsInCart(ids);
  } catch {
    /* ignore network errors on silent sync */
  }
}

function initCartHandlers() {
  if (document.documentElement.dataset.pcCartInit) return;
  document.documentElement.dataset.pcCartInit = 'true';

  const root = document.querySelector('[data-pc-cart-notification]');
  if (root) {
    root.querySelector('[data-pc-cart-notification-close]')?.addEventListener('click', hideCartNotification);
    root.querySelector('[data-pc-cart-notification-continue]')?.addEventListener('click', hideCartNotification);
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-pc-atc]');
    if (!btn || !(btn instanceof HTMLElement) || btn.disabled) return;

    event.preventDefault();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    // Prefer live quantity from the surrounding PDP form when present.
    const form = btn.closest('form');
    const qtyFromForm = form?.querySelector('[name="quantity"]');
    const quantity = Number(qtyFromForm?.value || btn.dataset.quantity || 1);

    const wasAdded = btn.dataset.state === 'added' || btn.classList.contains('pc-atc-btn--added');
    setAtcButtonState(btn, 'loading');

    pcAddToCart({
      id: Number(variantId),
      quantity: Math.max(1, quantity),
      title: btn.dataset.productTitle,
      image: btn.dataset.productImage,
      priceLabel: btn.dataset.productPrice,
    }, {
      showNotification: !btn.hasAttribute('data-pc-redirect-cart'),
    })
      .then(() => {
        markVariantAdded(variantId);
        if (btn.hasAttribute('data-pc-redirect-cart')) {
          const cartUrl =
            window.Shopify?.routes?.root != null
              ? `${window.Shopify.routes.root}cart`
              : '/cart';
          window.location.href = cartUrl;
          return;
        }
      })
      .catch((error) => {
        setAtcButtonState(btn, wasAdded ? 'added' : 'idle');
        window.alert(error.message || 'Could not add to cart');
      });
  });

  /**
   * Paris Corner PDP keeps a Shopify product form for wallets / accelerated checkout,
   * but Add to Cart is handled only via [data-pc-atc] → pcAddToCart → /cart/add.js.
   * Blocking form submit prevents Shopify's form-submit pixel from publishing a second
   * product_added_to_cart alongside the Ajax Cart API event.
   */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.classList.contains('pc-pdp__form') && !form.classList.contains('pc-atc-form')) return;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-pc-cart-bundle-remove]');
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
    event.preventDefault();
    btn.disabled = true;
    pcRemoveBundle(btn.dataset.keys, btn.dataset.code)
      .then(() => window.location.reload())
      .catch((error) => {
        btn.disabled = false;
        window.alert(error.message || 'Could not remove bundle');
      });
  });

  // On load: if already in cart, show Added without requiring a click.
  syncAtcButtonsWithCart();
  stripOrphanBundleCodes();
}

/**
 * Drop a BUNDLE5/10/… code when the cart no longer has that bundle.
 * Otherwise it can discount products it was not built for.
 */
async function stripOrphanBundleCodes() {
  if (!document.querySelector('.cart-page')) return;
  if (new URLSearchParams(window.location.search).has('pc_cart')) return;
  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return;
    const cart = await response.json();
    const codes = (cart.discount_codes || []).map((discount) => discount.code).filter(Boolean);
    const bundleCodes = codes.filter((code) => /^BUNDLE\d+$/i.test(code));
    if (!bundleCodes.length) return;

    const active = new Set(
      (cart.items || [])
        .map((item) => String(item.properties?._pc_bundle_code || '').toUpperCase())
        .filter(Boolean)
    );
    const orphan = bundleCodes.filter((code) => !active.has(code.toUpperCase()));
    if (!orphan.length) return;

    const keep = codes.filter((code) => !orphan.some((drop) => drop.toUpperCase() === code.toUpperCase()));
    const update = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: keep.join(',') }),
    });
    if (update.ok) window.location.reload();
  } catch {
    /* leave the cart as Shopify returned it */
  }
}

/**
 * Remove every line of a CREATE YOUR BUNDLE group and drop its tier discount code.
 * @param {string} [keysValue] - Comma-separated cart line keys
 * @param {string} [bundleCode] - Discount code applied for this bundle
 */
export async function pcRemoveBundle(keysValue, bundleCode) {
  const root = window.Shopify?.routes?.root || '/';
  const keys = String(keysValue || '')
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);
  if (!keys.length) return;

  const post = async (body) => {
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Could not update cart');
    return data;
  };

  const cart = await post({ updates: Object.fromEntries(keys.map((key) => [key, 0])) });

  const code = String(bundleCode || '').trim().toUpperCase();
  const stillHasBundle = (cart.items || []).some((line) => line.properties?._pc_bundle_id);
  if (!code || stillHasBundle) return;

  const remaining = (cart.discount_codes || [])
    .map((discount) => discount.code)
    .filter((existing) => existing && existing.toUpperCase() !== code);
  await post({ discount: remaining.join(',') });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartHandlers);
} else {
  initCartHandlers();
}

window.pcAddToCart = pcAddToCart;
window.pcApplyDiscountCode = pcApplyDiscountCode;
window.pcRemoveBundle = pcRemoveBundle;
window.pcShowCartNotification = showCartNotification;
